/**
 * Bundle 169 hotfix 2 - Pod stability detail modal.
 *
 * Renders the full `/admin/incidents/boot-health-report` payload as
 * four sections: Boot, /cards cache, Per-detector timings, Badge caches.
 *
 * The Refresh button re-fetches via the parent-supplied `onRefresh`
 * (pure re-fetch - does NOT bust caches; the tile is diagnostic, not a
 * cache-invalidation surface).
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RefreshCcw } from 'lucide-react';

const formatSecondsAgo = (s) => {
  if (s === null || s === undefined) return '-';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

const Section = ({ title, testid, children }) => (
  <section className="border border-neutral-200 rounded-md" data-testid={testid}>
    <div className="px-4 py-2 border-b border-neutral-100 bg-neutral-50">
      <h3 className="text-[11px] uppercase tracking-widest text-secondary-700">
        {title}
      </h3>
    </div>
    <div className="p-4 text-xs">{children}</div>
  </section>
);

const Row = ({ label, value }) => (
  <div className="flex items-baseline gap-4 py-1">
    <span className="text-neutral-500 min-w-[9rem]">{label}</span>
    <span className="text-primary-900 font-mono text-[11px] break-all">
      {value === null || value === undefined ? '-' : String(value)}
    </span>
  </div>
);

const DetectorTable = ({ timings }) => {
  const rows = Object.entries(timings || {})
    .sort((a, b) => (b[1] || 0) - (a[1] || 0));
  if (rows.length === 0) {
    return (
      <div className="text-neutral-500">
        No detector timings recorded yet - the /admin/health/cards cache
        has not been rebuilt this pod lifetime.
      </div>
    );
  }
  return (
    <table
      className="w-full text-xs" data-testid="pod-stability-detector-table"
    >
      <thead>
        <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
          <th className="text-left py-1 pr-4">Detector</th>
          <th className="text-right py-1">ms</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([name, ms]) => (
          <tr
            key={name}
            className="border-t border-neutral-100"
            data-testid={`pod-stability-detector-row-${name}`}
          >
            <td className="py-1 pr-4 font-mono text-[11px] text-primary-900">
              {name}
            </td>
            <td className={`py-1 text-right font-mono text-[11px] ${
              ms >= 3000 ? 'text-red-700 font-semibold'
                : ms >= 1500 ? 'text-amber-700 font-semibold'
                  : 'text-neutral-700'
            }`}>
              {ms}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const PodStabilityDetailModal = ({
  open, onClose, report, loading, error, onRefresh,
}) => {
  const boot = report?.boot || {};
  const cardsCache = report?.health_cards_cache || {};
  const brokenNames = report?.broken_names_cache || {};
  const badgeCache = report?.admin_users_badge_cache || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="pod-stability-modal"
      >
        <DialogHeader>
          <DialogTitle>Pod stability</DialogTitle>
          <DialogDescription>
            Bundle 169 boot-health report - event-loop health for the
            single-worker uvicorn process. Refresh re-fetches without
            busting caches.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div
            className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3"
            data-testid="pod-stability-modal-error"
          >
            {error}
          </div>
        ) : null}

        <div className="space-y-3">
          <Section title="Boot" testid="pod-stability-section-boot">
            <Row label="Boot at" value={boot.boot_at} />
            <Row label="Ready" value={boot.ready === true ? 'yes'
              : boot.ready === false ? 'no' : '-'} />
            <Row label="DB connected"
              value={boot.db_connected === true ? 'yes'
                : boot.db_connected === false ? 'no' : '-'} />
            <Row label="Startup duration (s)"
              value={boot.startup_duration_seconds} />
            <Row label="Previous uptime (s)"
              value={boot.prev_uptime_seconds} />
            <Row label="PID" value={boot.pid} />
            <Row label="App env" value={boot.app_env} />
          </Section>

          <Section title="/admin/health/cards cache"
            testid="pod-stability-section-cards">
            <Row label="TTL (s)" value={cardsCache.ttl_seconds} />
            <Row label="Cached age"
              value={formatSecondsAgo(cardsCache.cache_age_seconds)} />
            <Row label="Hits" value={cardsCache.hits} />
            <Row label="Misses" value={cardsCache.misses} />
            <Row label="Last build (ms)" value={cardsCache.last_build_ms} />
            <Row label="Cached card count"
              value={cardsCache.cached_card_count} />
          </Section>

          <Section title="Per-detector timings (last build)"
            testid="pod-stability-section-detectors">
            <DetectorTable
              timings={cardsCache.detector_timings_ms} />
          </Section>

          <Section title="Admin > Users badge caches"
            testid="pod-stability-section-badges">
            <Row label="TTL (s)" value={badgeCache.ttl_seconds} />
            {Object.entries(badgeCache.endpoints || {}).map(
              ([key, entry]) => (
                <div
                  key={key}
                  className="mt-2 pl-2 border-l border-neutral-100"
                  data-testid={`pod-stability-badge-${key}`}
                >
                  <div className="text-[11px] font-semibold text-primary-900">
                    {key}
                  </div>
                  <Row label="Hits" value={entry?.hits} />
                  <Row label="Misses" value={entry?.misses} />
                  <Row label="Cached age"
                    value={formatSecondsAgo(entry?.cache_age_seconds)} />
                </div>
              ),
            )}
          </Section>

          <Section title="Broken-names cache (Bundle 164)"
            testid="pod-stability-section-broken-names">
            <Row label="TTL (s)" value={brokenNames.ttl_seconds} />
            <Row label="Cached"
              value={brokenNames.cached === true ? 'yes'
                : brokenNames.cached === false ? 'no' : '-'} />
            <Row label="Count" value={brokenNames.count} />
          </Section>
        </div>

        <DialogFooter>
          <Button
            variant="outline" size="sm"
            onClick={onRefresh}
            disabled={loading}
            data-testid="pod-stability-modal-refresh"
          >
            <RefreshCcw
              className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
          <Button
            variant="default" size="sm" onClick={onClose}
            data-testid="pod-stability-modal-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PodStabilityDetailModal;
